import { useEffect, useRef, useState } from 'react';
import { Check } from 'lucide-react';
import { Sheet } from '../../components/ui/Sheet';
import { SegmentedControl } from '../../components/ui/SegmentedControl';
import { TextField } from '../../components/ui/TextField';
import { Button } from '../../components/ui/Button';
import { searchMappings, fetchMapping, updateMapping } from '../../api/dataApi';
import { queryClient, queryKeys } from '../../lib/queryClient';
import type { Mapping } from '../../types';

// Owner-only affordance opened from the compose bar's "+" (Add to
// message) sheet - points an EXISTING mapping (yours, or a public one
// someone else made) at this channel, so its incoming data starts
// landing here without recreating the whole integration from scratch.
// This edits the mapping in place (spec.channel, the same field every
// mapping source type reads as its destination - see backend/internal/
// webhookrecv/handler.go's comment) rather than cloning it: if that
// mapping was already feeding another channel, this redirects it away
// from there, which is exactly what was asked for, not a copy.
export function AttachMappingSheet({
  open,
  onClose,
  channelNickname,
  channelTitle,
  onAttached,
}: {
  open: boolean;
  onClose: () => void;
  channelNickname: string;
  channelTitle: string;
  onAttached: (mapping: Mapping) => void;
}) {
  const [scope, setScope] = useState<'mine' | 'public'>('mine');
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Mapping[]>([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState<Mapping | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const requestId = useRef(0);

  useEffect(() => {
    if (!open) return;
    setScope('mine');
    setQuery('');
    setResults([]);
    setSelected(null);
    setError('');
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const thisRequest = ++requestId.current;
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const page = await queryClient.query({
          queryKey: queryKeys.mappingSearch(scope, query, 0),
          queryFn: () => searchMappings(scope, query),
        });
        const hydrated = (await Promise.all(page.data.map((n) => fetchMapping(n)))).filter((m): m is Mapping => m !== null);
        if (requestId.current === thisRequest) setResults(hydrated);
      } catch {
        if (requestId.current === thisRequest) setResults([]);
      } finally {
        if (requestId.current === thisRequest) setSearching(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [open, scope, query]);

  const pointHere = async () => {
    if (!selected) return;
    setSaving(true);
    setError('');
    try {
      const updated = await updateMapping({ ...selected, spec: { ...selected.spec, channel: channelNickname } });
      onAttached(updated);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to point this mapping here - you may not own it.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet open={open} onClose={onClose} title="Point a mapping here">
      <div className="flex flex-col gap-4">
        <SegmentedControl
          options={[
            { value: 'mine' as const, label: 'My own' },
            { value: 'public' as const, label: 'Public' },
          ]}
          value={scope}
          onChange={setScope}
        />
        <TextField placeholder={scope === 'mine' ? 'Search my mappings' : 'Search public mappings'} value={query} onChange={(e) => setQuery(e.target.value)} />

        {selected && (
          <div className="rounded-xl border border-blue-500/40 bg-blue-500/5 px-3 py-2 text-xs text-black/60 dark:text-white/60">
            Incoming data for <span className="font-medium text-black/80 dark:text-white/80">{selected.title || selected.nickname}</span> will start
            landing in <span className="font-medium text-black/80 dark:text-white/80">{channelTitle}</span> - if it was already pointed at another
            channel, this redirects it away from there.
          </div>
        )}

        <div className="flex flex-col gap-1.5 max-h-56 overflow-y-auto">
          {searching && <span className="text-sm text-black/40 dark:text-white/40 py-2 text-center">Searching…</span>}
          {!searching && results.length === 0 && (
            <span className="text-sm text-black/40 dark:text-white/40 py-2 text-center">
              {query ? 'No mappings match that search.' : scope === 'mine' ? 'You have no mappings of your own yet.' : 'No public mappings match yet.'}
            </span>
          )}
          {!searching &&
            results.map((m) => (
              <button
                key={m.id}
                type="button"
                onClick={() => setSelected(m)}
                className={`flex items-center gap-2.5 rounded-xl border px-3 py-2 text-left ${
                  selected?.id === m.id
                    ? 'border-blue-500 bg-blue-500/5'
                    : 'border-black/10 dark:border-white/10 hover:bg-black/5 dark:hover:bg-white/5'
                }`}
              >
                <span className="flex-1 min-w-0">
                  <span className="block truncate text-sm font-medium">{m.title || m.nickname}</span>
                  <span className="block truncate text-xs text-black/40 dark:text-white/40">
                    {m.nickname} · {m.sourceType || 'unknown source'}
                  </span>
                </span>
                {selected?.id === m.id && <Check size={16} className="text-blue-600 dark:text-blue-400 shrink-0" />}
              </button>
            ))}
        </div>

        {error && <p className="text-sm text-red-500">{error}</p>}
        <Button onClick={pointHere} disabled={!selected} loading={saving}>
          Point here
        </Button>
      </div>
    </Sheet>
  );
}
